// The AUDIT console route — the INSTALLATION-WIDE trail (@erp/module-audit,
// db-audit), mounted at /audit exactly where modules/audit/src/manifest.ts
// points its nav entry. ONE route, no subtree: the module has one screen (the
// manifest's stated design — everything else the trail powers is EMBEDDED as
// RecordHistory on other modules' detail screens).
//
// This is NOT the library's sensitive-act register (/library/operations/audit,
// lib_audit_event) — that is LIB's own hand-written log of waives/overrides/
// deletions. THIS trail is the tier-1 gateway capture: every mutating API
// request across every module, entity-derived from the matched route pattern
// (ADR-004 §5). Both screens say so on-screen; this comment says so in code.
//
// GATING is per-screen (router.tsx's documented choice): the screen wraps
// itself in ConsoleLayout perm="audit:read" (AUDIT_CONSOLE_PERMS). NO loader —
// the feed rides audit:read, a tier the layout itself gates, but the filter
// state lives in screen signals (the AuditLog idiom), so an eager loader would
// warm a filterless page the screen may never read.
//
// PANEL URL CONTRACT (the library-operations precedent): the per-event viewer
// is `?panel=detail&panelId=<eventId>` against a CLOSED name set, healed by
// panelSearch — Back closes it, a pasted link opens straight into the event.
import { createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import { panelSearch } from "@erp/ui/side-panel";
import type { AnyRoute } from "@tanstack/solid-router";

/** The closed panel-name set for /audit (see the PANEL URL CONTRACT note). */
export const AUDIT_TRAIL_PANELS = ["detail"] as const;

/**
 * Build the `/audit` route under the portal's root route. Returns the routes to
 * spread into `rootRoute.addChildren([...])` (router.tsx).
 */
export function makeAuditRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const trailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "audit",
    validateSearch: (input: Record<string, unknown>) => panelSearch(AUDIT_TRAIL_PANELS, input),
  }).lazy(lazyRoute(() => import("../screens/Trail.js")));

  return [trailRoute];
}
